import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { motion } from 'framer-motion';
import { CheckCircle, Crown, Sparkles, Dog, Zap, Star, Calendar, PawPrint, Shield, Lock, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import { trackPixelEvent, PIXEL_EVENTS } from '../utils/pixelService.js';
import { trackPurchase } from '../utils/gaService.js';
import { trackHotjarEvent, setHotjarState } from '../utils/hotjarService.js';

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoaded, reload } = useUser();
  const [countdown, setCountdown] = useState(5);
  const [isRefreshing, setIsRefreshing] = useState(true);
  const sessionId = searchParams.get('session_id');
  const [showSessionId, setShowSessionId] = useState(false);
  
  // Use refs to track timers and state
  const timerRef = useRef(null);
  const backupTimerRef = useRef(null);
  const hasRedirected = useRef(false);
  const refreshAttempts = useRef(0);
  const hasTrackedPurchase = useRef(false);

  // Format session ID for display
  const formattedSessionId = sessionId ? 
    (sessionId.length > 12 ? `${sessionId.substring(0, 8)}...${sessionId.substring(sessionId.length - 4)}` : sessionId) 
    : null;

  // Function to refresh user data
  const refreshUserData = async () => {
    
    if (!isLoaded || !user) {
      return false;
    }
    
    try {
      const previousPlan = user.unsafeMetadata?.plan || 'Free';
      await reload();
      const currentPlan = user.unsafeMetadata?.plan;
      
      // Track purchase event if the plan has changed and we haven't tracked it yet
      if (currentPlan && currentPlan !== previousPlan && !hasTrackedPurchase.current) {
        // Definir precios de los planes
        const planPrices = {
          'Basic': 49,
          'Pro': 99,
          'Premium': 149
        };
        
        // Obtener el precio del plan actual
        const planPrice = planPrices[currentPlan] || 0;
        
        // Parámetros comunes para el evento de compra
        const purchaseParams = {
          content_name: `Plan: ${currentPlan}`,
          content_type: 'subscription',
          content_ids: [currentPlan],
          transaction_id: sessionId || `purchase_${Date.now()}`,
          value: planPrice,
          currency: 'MXN'
        };
        
        // Rastrear en Facebook Pixel
        trackPixelEvent(PIXEL_EVENTS.PURCHASE, purchaseParams);
        
        // Rastrear en Google Analytics
        trackPurchase(
          currentPlan,
          planPrice,
          sessionId || `purchase_${Date.now()}`,
          'MXN',
          { user_id: user.id }
        );
        
        // Rastrear en Hotjar
        trackHotjarEvent(`purchase_completed_${currentPlan.toLowerCase()}`);
        
        // Establecer estado en Hotjar para el usuario
        setHotjarState({
          current_plan: currentPlan,
          purchase_value: planPrice,
          user_id: user.id
        });
        
        hasTrackedPurchase.current = true;
      }
      
      // Set flag for components to know data was refreshed
      sessionStorage.setItem('justCompletedPayment', 'true');
      
      // Show success toast
      toast.success('¡Tu plan ha sido actualizado correctamente!', {
        duration: 5000,
        style: {
          background: '#F0FDF4',
          color: '#166534',
          padding: '16px',
          borderRadius: '8px',
        }
      });
      
      setIsRefreshing(false);
      return true;
    } catch (error) {
      refreshAttempts.current += 1;
      
      if (refreshAttempts.current < 3) {
        setTimeout(refreshUserData, 1000);
      } else {
        setIsRefreshing(false);
      }
      return false;
    }
  };

  // Handle redirection to home
  const redirectToHome = () => {
    if (hasRedirected.current) return;
    
    hasRedirected.current = true;
    
    // Clear any existing timers
    if (timerRef.current) clearInterval(timerRef.current);
    if (backupTimerRef.current) clearTimeout(backupTimerRef.current);
    
    // Set flag for components to know data was refreshed
    sessionStorage.setItem('justCompletedPayment', 'true');
    sessionStorage.setItem('redirectTimestamp', Date.now().toString());
    
    // Force a full page reload by using window.location instead of navigate
    setTimeout(() => {
      window.location.href = '/dog-analyzer';
    }, 100);
  };

  // Initial setup - refresh user data and set up timers
  useEffect(() => {
    
    // Immediately try to refresh user data
    refreshUserData();
    
    // Set up countdown timer
    timerRef.current = setInterval(() => {
      setCountdown(prevCount => {
        return prevCount - 1;
      });
    }, 1000);
    
    // Set up backup timer that will redirect regardless after 8 seconds
    backupTimerRef.current = setTimeout(() => {
      redirectToHome();
    }, 8000);
    
    // Cleanup function
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (backupTimerRef.current) clearTimeout(backupTimerRef.current);
    };
  }, []); // Empty dependency array - run once on mount

  // Watch countdown and trigger redirect when it reaches 0
  useEffect(() => {
    if (countdown <= 0) {
      redirectToHome();
    }
  }, [countdown]);

  // Confetti animation
  useEffect(() => {
    const triggerConfetti = async () => {
      try {
        const { default: confettiLib } = await import('canvas-confetti');
        
        const count = 200;
        const defaults = {
          origin: { y: 0.7 },
          zIndex: 999
        };

        function fire(particleRatio, opts) {
          confettiLib({
            ...defaults,
            ...opts,
            particleCount: Math.floor(count * particleRatio)
          });
        }

        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.2, { spread: 60 });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
        fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
        fire(0.1, { spread: 120, startVelocity: 45 });
      } catch (error) {
        console.error('Failed to load confetti:', error);
      }
    };

    triggerConfetti();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white overflow-hidden px-4 py-6 sm:px-0 sm:py-0">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 1 }}
          className="absolute -top-20 -right-20 w-40 h-40 sm:w-64 sm:h-64 rounded-full bg-amber-200/20 blur-3xl"
        />
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="absolute -bottom-32 -left-20 w-60 h-60 sm:w-80 sm:h-80 rounded-full bg-orange-200/20 blur-3xl"
        />
        
        {/* Floating icons - fewer on mobile */}
        {[Crown, Star, PawPrint, Shield].map((Icon, index) => (
          <motion.div
            key={index}
            initial={{ 
              opacity: 0, 
              x: Math.random() * 100 - 50,
              y: Math.random() * 100 - 50,
            }}
            animate={{ 
              opacity: [0, 0.7, 0.5],
              x: Math.random() * 200 - 100,
              y: Math.random() * 200 - 100,
              rotate: Math.random() * 360
            }}
            transition={{ 
              duration: 10 + Math.random() * 20,
              repeat: Infinity,
              repeatType: "reverse",
              delay: index * 0.5
            }}
            className="absolute text-amber-300/30 hidden sm:block"
            style={{
              top: `${20 + Math.random() * 60}%`,
              left: `${20 + Math.random() * 60}%`,
              fontSize: `${30 + Math.random() * 40}px`
            }}
          >
            <Icon size={20 + Math.random() * 30} />
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full mx-auto p-5 sm:p-8 bg-white rounded-xl shadow-lg relative z-10 border border-amber-100"
      >
        {/* Success badge with animation */}
        <div className="absolute -top-5 -right-5 hidden sm:block">
          <motion.div
            initial={{ rotate: -10, scale: 0 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
            className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1"
          >
            <Lock className="w-3 h-3" />
            <span>Pago Seguro</span>
          </motion.div>
        </div>

        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="mx-auto mb-4 sm:mb-6 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-green-100 to-green-50 rounded-full flex items-center justify-center shadow-md"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 5, 0, -5, 0]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
            </motion.div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent"
          >
            ¡Pago Exitoso!
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6"
          >
            Tu suscripción ha sido procesada correctamente.
            {sessionId && import.meta.env.DEV && (
              <div className="mt-2 flex flex-col items-center">
                <button 
                  onClick={() => setShowSessionId(!showSessionId)}
                  className="text-xs text-amber-600 hover:text-amber-700 underline flex items-center gap-1"
                >
                  {showSessionId ? "Ocultar" : "Mostrar"} ID de transacción
                </button>
                {showSessionId && (
                  <div className="mt-1 p-2 bg-gray-50 rounded-md text-xs text-gray-500 font-mono break-all max-w-full overflow-x-auto">
                    {sessionId}
                  </div>
                )}
              </div>
            )}
          </motion.p>
          
          {/* Transaction details card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-100 shadow-sm"
          >
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
              </motion.div>
              <h3 className="text-sm sm:text-base font-medium text-amber-800">
                {isRefreshing ? 'Actualizando tu información...' : '¡Tu plan ha sido actualizado!'}
              </h3>
            </div>
            
            <div className="flex flex-col gap-2 mt-3">
              <div className="flex items-center justify-between p-2 bg-white/80 rounded-lg border border-amber-50">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-xs sm:text-sm text-gray-700">Beneficios Premium</span>
                </div>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </motion.div>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-white/80 rounded-lg border border-amber-50">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-500" />
                  <span className="text-xs sm:text-sm text-gray-700">Acceso Inmediato</span>
                </div>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                >
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </motion.div>
              </div>

              <div className="flex items-center justify-between p-2 bg-white/80 rounded-lg border border-amber-50">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-500" />
                  <span className="text-xs sm:text-sm text-gray-700">Soporte Prioritario</span>
                </div>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                >
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </motion.div>
              </div>
            </div>

            {/* Transaction security badge */}
            <div className="mt-3 flex items-center justify-center gap-2 bg-white/80 py-2 px-3 rounded-full border border-green-100 mx-auto w-fit">
              <Lock className="w-3 h-3 text-green-600" />
              <span className="text-xs text-green-700">Transacción segura y verificada</span>
            </div>
          </motion.div>
          
          {/* Progress bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="relative h-2 bg-gray-100 rounded-full overflow-hidden mb-2 sm:mb-3"
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(5 - countdown) * 20}%` }}
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-400 to-orange-400"
            />
          </motion.div>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6"
          >
            Serás redirigido automáticamente en <span className="font-semibold text-amber-600">{countdown}</span> segundos
          </motion.p>
          
          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={redirectToHome}
            className="mt-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors shadow-md flex items-center justify-center gap-2 w-full text-sm sm:text-base"
          >
            <Dog className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Ir al analizador</span>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default Success; 